import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { debounce, next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import onResize from 'ember-on-resize-modifier/modifiers/on-resize';
import { provide } from 'ember-provide-consume-context';
import { and, or } from 'ember-truth-helpers';

import Loader from 'umg-ember-backend/components/ui/loader';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

interface WindowSignature {
  Args: {
    isBottombarVisible?: boolean;
    isLoading?: boolean;
    isSeamless?: boolean;
    onBodyScroll?: (scrollTop: number) => void;
    scrollPosition?: number;
  };
  Element: HTMLDivElement;
  Blocks: {
    title: [{ isCompact: boolean }];
    overline: [{ isCompact: boolean }];
    description: [{ isCompact: boolean }];
    toolbar: [{ isCompact: boolean }];
    body: [];
    bottombar: [];
  };
}

export default class UiWindowComponent extends Component<WindowSignature> {
  // Defaults
  @tracked isToolbarCompact = false;

  // Constructor
  constructor(owner: Owner, args: WindowSignature['Args']) {
    super(owner, args);

    if (this.args.scrollPosition) {
      this.isToolbarCompact = true;
    }
  }

  // Getter and setter
  get isBottombarVisible() {
    return this.args.isBottombarVisible ?? true;
  }

  get isLoading() {
    return this.args.isLoading ?? false;
  }

  @provide('window')
  get isWindowCompact() {
    return this.isToolbarCompact;
  }

  // Functions
  onUiWindowScroll = (event: Event) => {
    const target = event.target as HTMLDivElement;
    debounce(this, this.updateScrollPosition, { element: target }, 200);

    if (target?.scrollTop) {
      this.isToolbarCompact = true;
    } else {
      this.isToolbarCompact = false;
    }
  };

  restoreScrollPosition = (element: HTMLDivElement) => {
    next(() => {
      if (!this.isLoading) {
        element.scrollTop = this.args.scrollPosition ?? 0;
      }
    });
  };

  onUiWindowResize = (event: Event) => {
    const target = event.target as HTMLDivElement;
    const scrollOffset = target?.scrollHeight - target.clientHeight;

    // TODO: 100 is a magic number, find a better way to do this
    if (scrollOffset < 100) {
      target.style.paddingBottom = '100px';
    }
  };

  updateScrollPosition = ({ element }: { element: HTMLDivElement }) => {
    if (this.args.onBodyScroll) {
      this.args.onBodyScroll(element.scrollTop);
    }
  };

  // Template
  <template>
    <div
      class={{bem
        (hash
          is-compact=this.isToolbarCompact
          is-seamless=@isSeamless
          is-loading=@isLoading
        )
      }}
      data-selector='window'
      ...attributes
    >
      {{#if (or (has-block 'title') (has-block 'toolbar'))}}
        <header class={{bem 'header'}}>
          <div class={{bem 'title'}}>
            {{#if (has-block 'overline')}}
              <div class={{bem 'overline'}} data-test-window-overline>
                {{yield (hash isCompact=this.isToolbarCompact) to='overline'}}
              </div>
            {{/if}}

            {{#if (has-block 'title')}}
              <h1 class={{bem 'main'}} data-test-window-main>
                {{yield (hash isCompact=this.isToolbarCompact) to='title'}}
              </h1>
            {{/if}}
          </div>

          {{#if (has-block 'description')}}
            <div class={{bem 'description'}}>
              {{yield (hash isCompact=this.isToolbarCompact) to='description'}}
            </div>
          {{/if}}

          {{#if (has-block 'toolbar')}}
            <div class={{bem 'toolbar'}}>
              {{yield (hash isCompact=this.isToolbarCompact) to='toolbar'}}
            </div>
          {{/if}}
        </header>
      {{/if}}

      {{#if (has-block 'body')}}
        <div class={{bem 'body'}}>
          <div
            class={{bem 'scroller'}}
            {{on 'scroll' this.onUiWindowScroll}}
            {{onResize this.onUiWindowResize}}
            {{didInsert this.restoreScrollPosition}}
            {{didUpdate this.restoreScrollPosition @isLoading}}
          >
            {{yield to='body'}}
          </div>

          <Loader @isWaiting={{@isLoading}} />
        </div>
      {{/if}}

      {{#if (and (has-block 'bottombar') this.isBottombarVisible)}}
        <div class={{bem 'bottombar'}} data-test-window-bottombar>
          {{yield to='bottombar'}}
        </div>
      {{/if}}

      <div
        class={{bem 'modals'}}
        data-test-window-modals
        data-selector='window-modals'
      >
        {{! Placeholder for modals }}
      </div>
    </div>
  </template>
}
